import type { ReactNode } from 'react';
import { cx } from '../cx';
import { PageContainer, type PageContainerWidth } from '../PageContainer/PageContainer';

export type CalculatorTemplateProps = {
  header: ReactNode;
  /** Input + RangeSlider, the municipality Select, period FilterChips. */
  inputs: ReactNode;
  /** The privacy note: church membership and union fees stay in the browser. */
  privacyNote?: ReactNode;
  /** The net figure (a Readout or StatTile). */
  result?: ReactNode;
  /** The Iceberg, with every assumption listed. */
  iceberg?: ReactNode;
  /** The primary action into matching jobs. */
  action?: ReactNode;
  /**
   * `split` (default): inputs left, result and iceberg right, for a compact figure.
   * `stacked`: the inputs in one card above, then the result and a full-width picture, for a wide
   * panorama that needs the whole page width (the tax iceberg scene).
   */
  layout?: 'split' | 'stacked';
  /** `stacked` only: the primary input, shown alone above the picture on phones (the rest of `inputs` moves below the picture there). */
  lead?: ReactNode;
  /** Width of the page frame. */
  width?: PageContainerWidth;
  /** `stacked` only: answers and reference blocks below the picture (raise, breakdown, FAQ, sources). */
  children?: ReactNode;
  className?: string;
};

const INPUT_CARD = 'flex flex-col gap-5 rounded-jc-container border border-jc-card-border bg-jc-surface p-5 md:p-6';

/** The tax iceberg and net salary calculator. Figures are calculated, never typed. */
export function CalculatorTemplate({
  header,
  inputs,
  privacyNote,
  result,
  iceberg,
  action,
  layout = 'split',
  lead,
  width,
  children,
  className,
}: CalculatorTemplateProps) {
  if (layout === 'stacked') {
    return (
      <PageContainer width={width} className={className}>
        <div className="flex flex-col gap-jc-block py-jc-section">
          {header}
          <div className="flex flex-col gap-jc-block">
            {lead ? <div className={cx(INPUT_CARD, 'md:hidden')}>{lead}</div> : null}
            <div className={cx(INPUT_CARD, Boolean(lead) && 'order-last md:order-first')}>
              {inputs}
              {privacyNote ? <div className="text-xs text-jc-ink-secondary">{privacyNote}</div> : null}
            </div>
            {result}
            {iceberg}
            {action}
          </div>
          {children}
        </div>
      </PageContainer>
    );
  }
  return (
    <PageContainer width={width} className={className}>
      <div className="flex flex-col gap-jc-block py-jc-section">
        {header}
        <div className="grid items-start gap-jc-block lg:grid-cols-5">
          <div className={cx(INPUT_CARD, 'lg:col-span-2')}>
            {inputs}
            {privacyNote ? <div className="text-xs text-jc-ink-secondary">{privacyNote}</div> : null}
          </div>
          <div className="flex flex-col gap-jc-block lg:col-span-3">
            {result}
            {iceberg}
            {action}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
